import { useEffect, useState } from "react";
import api from "@/lib/api";
import { AthleteCard } from "@/components/AthleteCard";
import { EmptyState } from "@/components/EmptyState";
import { ExportXlsxButton } from "@/components/ExportXlsxButton";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Users, Flame } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";

const ISLANDS = ["Tahiti", "Moorea", "Bora Bora", "Raiatea", "Tahaa", "Huahine", "Rangiroa", "Hiva Oa", "Nuku Hiva", "Tubuai"];

export default function AthletesPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  // V13.31 — Compteurs par origine (self_created vs imported) pour afficher
  // le séparateur visuel "Fiches revendiquées / Résultats officiels référencés".
  const [countsByOrigin, setCountsByOrigin] = useState({ self_created: 0, imported: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [discipline, setDiscipline] = useState("all");
  const [island, setIsland] = useState("all");
  const [disciplines, setDisciplines] = useState([]);

  // V13.24 — Bloc "Actifs cette année" (top des athlètes ayant du palmarès sur l'année en cours)
  const [activeYear, setActiveYear] = useState(null);
  const [activeItems, setActiveItems] = useState([]);
  const [activeLoading, setActiveLoading] = useState(true);

  useEffect(() => { api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])); }, []);

  // Chargement "Actifs cette année" — une seule fois (indépendant des filtres)
  useEffect(() => {
    setActiveLoading(true);
    api.get("/athletes/featured/active-this-year", { params: { limit: 12 } })
      .then((r) => {
        setActiveItems(r.data.items || []);
        setActiveYear(r.data.year || null);
      })
      .catch(() => { setActiveItems([]); })
      .finally(() => setActiveLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    // V11.20 — Bugfix : la limite doit couvrir tout l'effectif publié.
    // Le backend accepte jusqu'à 200 (`Query(50, le=200)`).
    const params = { limit: 200 };
    if (q) params.q = q;
    if (discipline !== "all") params.discipline = discipline;
    if (island !== "all") params.island = island;
    const t = setTimeout(
      () => api.get("/athletes", { params }).then((r) => {
        setItems(r.data.items || []);
        setTotal(Number(r.data.count || 0));
        setCountsByOrigin(r.data.counts_by_origin || { self_created: 0, imported: 0 });
      }).finally(() => setLoading(false)),
      250,
    );
    return () => clearTimeout(t);
  }, [q, discipline, island]);

  // V13.24 — On n'affiche la rangée "Actifs cette année" QUE si l'annuaire est
  // en mode navigation libre (aucun filtre ni recherche appliqué). Sinon, l'utilisateur
  // cherche quelque chose de précis → on cache pour éviter la confusion.
  const filtersActive = Boolean(q || discipline !== "all" || island !== "all");
  const showActiveStrip = !filtersActive && activeItems.length > 0;

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">Athlètes</h1>
          <p className="text-sm text-muted-foreground mt-1">Les sportifs du Fenua — crée ta fiche en quelques minutes.</p>
        </div>
        <Link to="/creer-ma-fiche" data-testid="athletes-create-cta">
          <Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">Créer ma fiche</Button>
        </Link>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-6">
        <Input data-testid="athletes-search-input" placeholder="Nom, surnom…" value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={discipline} onValueChange={setDiscipline}>
          <SelectTrigger data-testid="athletes-discipline-select"><SelectValue placeholder="Discipline" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les disciplines</SelectItem>
            {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={island} onValueChange={setIsland}>
          <SelectTrigger data-testid="athletes-island-select"><SelectValue placeholder="Île" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les îles</SelectItem>
            {ISLANDS.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center justify-end mb-4">
        <ExportXlsxButton
          endpoint="/exports-xlsx/athletes"
          params={{ q, discipline, island }}
          filename="AITO_athletes.xlsx"
          testid="athletes-export-xlsx"
        />
      </div>

      {/* V13.24 — Rangée "Actifs cette année" */}
      {(activeLoading || showActiveStrip) && (
        <section
          className="mb-8 rounded-2xl border border-[hsl(var(--aito-gold))]/25 bg-gradient-to-br from-[hsl(var(--aito-gold))]/8 via-transparent to-transparent p-4 sm:p-5"
          data-testid="active-this-year-strip"
          aria-label="Athlètes actifs cette année"
        >
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2 min-w-0">
              <div className="shrink-0 h-9 w-9 rounded-full bg-[hsl(var(--aito-gold))]/20 border border-[hsl(var(--aito-gold))]/40 flex items-center justify-center">
                <Flame className="h-4 w-4 text-[hsl(var(--aito-gold))]" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <h2 className="font-display font-semibold text-lg sm:text-xl leading-tight">
                  Actifs cette année
                  {activeYear && (
                    <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold bg-[hsl(var(--aito-gold))]/20 text-[hsl(var(--aito-gold))] align-middle" data-testid="active-this-year-badge">
                      {activeYear}
                    </span>
                  )}
                </h2>
                <p className="text-xs text-muted-foreground">
                  Sportifs polynésiens ayant un résultat officiel sur l'année en cours.
                </p>
              </div>
            </div>
            {activeItems.length > 0 && (
              <span className="text-xs font-medium text-muted-foreground" data-testid="active-this-year-count">
                {activeItems.length} athlète{activeItems.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {activeLoading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {activeItems.slice(0, 8).map((a) => (
                <AthleteCard key={`active-${a.id}`} athlete={a} />
              ))}
            </div>
          )}
        </section>
      )}

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-64 rounded-xl" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Users} title="Aucun athlète trouvé."
          description="Sois l'un des premiers à créer ta fiche athlète sur AITO.PF."
          action={<Link to="/creer-ma-fiche"><Button className="bg-[hsl(var(--aito-lagoon))] text-black hover:bg-[hsl(var(--aito-lagoon-2))]">Créer ma fiche</Button></Link>}
        />
      ) : (
        <>
          {showActiveStrip && (
            <div className="flex items-center gap-2 mb-3">
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tous les athlètes</span>
              <span className="h-px flex-1 bg-border" aria-hidden="true" />
            </div>
          )}
          <div className="text-xs text-muted-foreground mb-3" data-testid="athletes-count">
            {items.length === total
              ? `${total} athlète${total > 1 ? "s" : ""}`
              : `${items.length} affichés · ${total} au total`}
          </div>
          {/* V13.31 — Deux zones distinctes pour ne PAS "noyer" les fiches
              revendiquées au milieu des résultats importés officiels.
              - Zone 1 : self_created (fiches créées/revendiquées par les athlètes)
              - Zone 2 : imported_unclaimed / legacy_review (résultats officiels référencés)
              Chaque zone garde son propre tri interne par fraîcheur. Les
              séparateurs n'apparaissent que si les DEUX zones ont du contenu
              dans l'affichage courant. */}
          {(() => {
            const selfItems = items.filter((a) => a.origin_type === "self_created");
            const importedItems = items.filter((a) => a.origin_type !== "self_created");
            const hasBoth = selfItems.length > 0 && importedItems.length > 0;
            return (
              <>
                {selfItems.length > 0 && (
                  <section aria-labelledby="claimed-heading" data-testid="section-claimed">
                    {hasBoth && (
                      <h2 id="claimed-heading" className="flex items-center gap-2 mb-3 mt-1">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-[hsl(var(--aito-lagoon))]/15 text-[hsl(var(--aito-lagoon))] border border-[hsl(var(--aito-lagoon))]/30">
                          Fiches d'athlètes
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {selfItems.length} sportif{selfItems.length > 1 ? "s" : ""} en ligne
                          {countsByOrigin.self_created !== selfItems.length && ` · ${countsByOrigin.self_created} au total`}
                        </span>
                        <span className="h-px flex-1 bg-border" aria-hidden="true" />
                      </h2>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {selfItems.map((a) => <AthleteCard key={a.id} athlete={a} />)}
                    </div>
                  </section>
                )}

                {importedItems.length > 0 && (
                  <section aria-labelledby="imported-heading" data-testid="section-imported" className={hasBoth ? "mt-10" : ""}>
                    {hasBoth && (
                      <h2 id="imported-heading" className="flex items-center gap-2 mb-3">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-800/40">
                          Résultats officiels référencés
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Fiches minimales issues d'imports officiels · en attente de revendication
                        </span>
                        <span className="h-px flex-1 bg-border" aria-hidden="true" />
                      </h2>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                      {importedItems.map((a) => <AthleteCard key={a.id} athlete={a} />)}
                    </div>
                  </section>
                )}
              </>
            );
          })()}
        </>
      )}
    </div>
  );
}
